<!--
  SupportPeopleCard.svelte — who is on a ticket: its owner, and the people added
  to it.

  Participants need no licence of their own. They read the public thread,
  download its attachments, reply while the owner's support is active and get
  its emails — which is why adding someone is done by the address of an account
  they already hold, and why the server refuses an address nobody uses rather
  than inviting a stranger in.

  Who may change the list is the server's decision, carried in the ticket's
  permissions: the owner (customer side) or any agent (desk side) manages it,
  and a participant may only leave. Both destructive actions confirm first,
  since removing someone takes the ticket out of their view at once.
-->
<script lang="ts">
	import { Button, Input, Spinner } from 'flowbite-svelte';
	import AbstractCard from './AbstractCard.svelte';
	import ConfirmButton from './ConfirmButton.svelte';
	import RoutedLink from './RoutedLink.svelte';
	import type { SupportPerson } from '../AppTypes';
	import { SUPPORT_MAX_PARTICIPANTS, resolveSupportAvatarURL } from '../support/supportFormat';
	import site from '../site';
	import { t } from '../i18n';

	interface Props {
		/** Who filed the ticket — shown as its owner. */
		requester: SupportPerson;
		/** The people added to it besides the owner. */
		participants: SupportPerson[];
		/** Whether the viewer may add and remove people. */
		canManage?: boolean;
		/** Whether the viewer may leave — true only for a participant. */
		canLeave?: boolean;
		/** Whether a change is in flight. */
		busy?: boolean;
		/** Adds someone by email address. Resolves whether it worked, so the field knows to clear. */
		onadd?: (_email: string) => Promise<boolean>;
		/** Removes a participant. */
		onremove?: (_person: SupportPerson) => void;
		/** Takes the viewer off the ticket. */
		onleave?: () => void;
	}

	let {
		requester,
		participants,
		canManage = false,
		canLeave = false,
		busy = false,
		onadd,
		onremove,
		onleave
	}: Props = $props();

	/** The address being typed. */
	let email = $state('');

	/** Whether the ticket already holds as many people as it may. */
	let isFull = $derived(participants.length >= SUPPORT_MAX_PARTICIPANTS);

	/** Submits the address; the field is kept on failure so a typo can be fixed rather than retyped. */
	async function add(event: SubmitEvent): Promise<void> {
		event.preventDefault();
		const address = email.trim();
		/* Nothing to add. */
		if (!address || !onadd) return;
		/* Cleared only once the person is really on the ticket. */
		if (await onadd(address)) email = '';
	}

	/** Up to two initials, for someone with no avatar. */
	function initials(person: SupportPerson): string {
		return person.name
			.trim()
			.split(/\s+/)
			.filter(Boolean)
			.slice(0, 2)
			.map((part) => part[0].toUpperCase())
			.join('');
	}
</script>

{#snippet personRow(person: SupportPerson, isOwner: boolean)}
	{@const avatar = resolveSupportAvatarURL(person.avatarURL, site.backend.baseURL)}
	<li class="flex items-center gap-2" data-person={person.userUUID}>
		{#if avatar}
			<img src={avatar} alt="" class="h-7 w-7 shrink-0 rounded-full object-cover" />
		{:else}
			<div
				class="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-gray-200 text-[11px] font-semibold text-gray-700 dark:bg-gray-700 dark:text-gray-200"
				aria-hidden="true"
			>
				{initials(person)}
			</div>
		{/if}
		<div class="min-w-0 grow">
			<p class="truncate font-medium">
				<!-- Only agents are sent the numeric id, so only they get a link to the account. -->
				{#if person.id !== null}
					<RoutedLink to="/Users/{person.id}">{person.name}</RoutedLink>
				{:else}
					{person.name}
				{/if}
				{#if isOwner}
					<span class="text-default ms-1 text-xs font-normal">{$t('Owner')}</span>
				{/if}
			</p>
			{#if person.email}
				<p class="text-default text-xs break-all">{person.email}</p>
			{/if}
		</div>
		{#if !isOwner && canManage}
			<ConfirmButton
				size="xs"
				color="alternative"
				class="!p-1.5"
				disabled={busy}
				confirmText={$t('Remove {{name}} from this ticket?', { name: person.name })}
				onconfirm={() => onremove?.(person)}
			>
				<i class="fa-solid fa-xmark"></i><span class="sr-only">{$t('Remove {{name}}', { name: person.name })}</span>
			</ConfirmButton>
		{/if}
	</li>
{/snippet}

<AbstractCard class="max-w-none gap-3 text-sm">
	<div class="flex items-center justify-between">
		<p class="card-label">{$t('People')}</p>
		{#if busy}<Spinner size="4" />{/if}
	</div>
	<ul class="space-y-2" aria-label={$t('People')}>
		{@render personRow(requester, true)}
		{#each participants as person (person.userUUID)}
			{@render personRow(person, false)}
		{/each}
	</ul>

	{#if canManage}
		{#if isFull}
			<p class="text-default text-xs">
				{$t('A ticket can have at most {{count}} people besides its owner.', { count: SUPPORT_MAX_PARTICIPANTS })}
			</p>
		{:else}
			<form class="flex items-center gap-2" onsubmit={add}>
				<div class="min-w-0 flex-1">
					<Input
						type="email"
						size="sm"
						bind:value={email}
						placeholder={$t('Email address')}
						aria-label={$t('Add someone by email')}
						disabled={busy}
					/>
				</div>
				<Button type="submit" size="sm" disabled={busy || !email.trim()}>{$t('Add')}</Button>
			</form>
			<p class="text-default text-xs">
				{$t('People you add need an Abstract ID account. They can read the ticket, reply to it and get its emails.')}
			</p>
		{/if}
	{/if}

	{#if canLeave}
		<ConfirmButton
			size="sm"
			color="alternative"
			disabled={busy}
			confirmText={$t('Leave this ticket? You will no longer be able to see it.')}
			onconfirm={() => onleave?.()}
		>
			<i class="fa-solid fa-right-from-bracket me-2"></i>{$t('Leave ticket')}
		</ConfirmButton>
	{/if}
</AbstractCard>
